/**
 * Shared axios instance for all API calls.
 *
 * - Sends cookies on every request (`withCredentials: true`)
 * - Attaches `X-CSRF-Token` header on state-changing requests (POST/PUT/PATCH/DELETE)
 *   by reading the `__csrf` non-HttpOnly cookie set by `GET /auth/csrf-token`
 * - On 401 (session expired), clears auth state and redirects to /login?session_expired=true
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/** Read a cookie value by name from document.cookie. Returns null if not found. */
function getCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Attach X-CSRF-Token on mutating requests
apiClient.interceptors.request.use((config) => {
  const method = (config.method ?? '').toLowerCase();

  if (MUTATION_METHODS.has(method)) {
    const csrfToken = getCookie('__csrf');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  return config;
});

// On session-expired 401, redirect to login
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url: string = error.config?.url ?? '';
      // /auth/me returns 401 for unauthenticated users — expected, not a session expiry
      const isAuthCheck = url.includes('/auth/me');
      // Avoid redirect loop on the login page itself
      if (!isAuthCheck && !window.location.pathname.includes('/login')) {
        window.location.href = '/login?session_expired=true';
      }
    }
    return Promise.reject(error);
  },
);
